/**
 * The role of this module is to stash all single product calls by Id that are made
 * within a timeframe, convert all of them in a single productsById.
 *
 * For each separate request, the ID is merged with the previously requested products IDs
 * and a new Promise is generated, which is resolved once the response gets back.
 */

import { ICoreProduct, IItem, ItemTypes } from './ProdcatApiSdk';

export interface IQueryStack {
    productId: string;
    resolver(D: { product: ICoreProduct }): void;
    rejecter(error?: Error): void;
}

export class ProductQueryStashingService {
    public productsStack: string[] = [];
    private queryStack: IQueryStack[] = [];
    private debounceTimeout: number;

    /**
     * The most light debounce implementation
     */
    public debounce = (callback: () => void, timeoutValue: number) => {
        clearTimeout(this.debounceTimeout);

        this.debounceTimeout = window.setTimeout(callback, timeoutValue);
    };

    /**
     * Creates a promise for the current product request that will be resolved
     * with the correct product when the query response is received
     */
    public createQueryPromise = (productId: string) => {
        const requestPromise = new Promise((resolver, rejecter) => {
            this.queryStack.push({
                productId,
                resolver,
                rejecter
            });
        });

        this.mergeIds(productId);

        return <Promise<ICoreProduct>>requestPromise;
    };

    /**
     * Will generate a simple IItem array that will get passed to the
     * getProductsById method to start the products query.
     */
    public prepareProductsQuery = () => {
        const items: IItem[] = this.productsStack.map(productId => ({
            type: ItemTypes.product,
            params: {
                id: productId
            }
        }));

        const queue = this.queryStack.slice();

        // Once we are ready to make the get products call, quickly empty the stacks
        // so that if any new query lost this train it will get stacked for a future
        // endpoint call
        this.queryStack = [];
        this.productsStack = [];

        return {
            items,
            queue
        };
    };

    /**
     * Resolves the queue promises for each product request. It gets from the ProdcatApi
     * the query response and starts sharing products back to users that requested them
     */
    public resolveQueue = (products: ICoreProduct[], queue: IQueryStack[]) => {
        const productsMap: { [key: string]: ICoreProduct } = {};

        // map products by id for ease of access
        if (products) {
            products.forEach(item => (productsMap[item.product_id] = item));
        }

        queue.forEach((query: IQueryStack) => {
            if (productsMap[query.productId]) {
                query.resolver({
                    // let's keep products objects unique
                    product: { ...productsMap[query.productId] }
                });
            } else {
                query.rejecter(new Error('product not found'));
            }
        });
    };

    /**
     * We stash the requested ID inside an array to have them ready for the
     * endpoint query
     */
    private mergeIds = (newId: string) => {
        if (this.productsStack.indexOf(newId) === -1) {
            this.productsStack.push(newId);
        }
    };
}
